export const ItemType = {
    DEPOSIT: 'DEPOSIT',
    WITHDRAWAL: 'WITHDRAWAL',
    TRANSFER: 'TRANSFER',
} as const;

export type ItemType = (typeof ItemType)[keyof typeof ItemType];

export const ExportStatus = {
    DRAFT: 'DRAFT',
    PENDING: 'PENDING',
    CLEARED: 'CLEARED',
    RECONCILED: 'RECONCILED',
    REJECTED: 'REJECTED',
} as const;

export type ExportStatus = (typeof ExportStatus)[keyof typeof ExportStatus];

export interface TrustShieldExportItem {
    hash: string;
    type: ItemType;
    amountCents: number;
    description: string;
    payee_name?: string;
    payee_address?: string;
    payee_payor?: string;
    instrumentNumber?: string;
    defectDescription?: string;
}

export interface TrustShieldExportPayload {
    appId: number;
    fileNumber: string;
    clientName: string;
    fileCreationDate: string;
    items: TrustShieldExportItem[];
    singleItem?: boolean;
    singleItemType?: 'deposit' | 'withdrawal';
    clickedHash?: string;
    isNewDeposit?: boolean;
    isNewPayment?: boolean;
    preFilled?: {
        transactionDate: string;
        trustAccount: string;
    };
    // When launched from the closing dashboard, the legacy iframe isn't loaded
    // with this file's XML, so the puracct-side mirror helpers can't reliably
    // write a matching Defect/IssueAdjust. Set this flag to suppress the
    // "+ Add a new receipt/payment" affordance in those cases — only existing
    // pending items can be exported until the user opens the file directly.
    fromClosingDashboard?: boolean;
}

// Which trust transfer surface the user wants on the trust side. Chosen via the
// pill at the top of the TransferItemSelectionModal and forwarded verbatim so the
// trust-side EmbeddedTransferFormHandler can open the right screen directly
// (matching its own `TransferType` literals — keep these two in sync).
export const TransferType = {
    MATTER_TO_MATTER: 'MATTER_TO_MATTER',
    TRUST_TO_TRUST: 'TRUST_TO_TRUST',
} as const;

export type TransferType = (typeof TransferType)[keyof typeof TransferType];

export const TRANSFER_TYPE_LABEL: Record<TransferType, string> = {
    MATTER_TO_MATTER: 'Matter to Matter',
    TRUST_TO_TRUST: 'Trust to Trust',
};

// One addable item on the transfer modal. Read-only snapshot collected from the
// Funds In (receipt) and Funds Out (payment) rows — id is a synthetic per-open
// key for add-tracking, unique across the whole `items` array (these items are
// never exported; clicking "Add" only folds the amount into the transfer total).
// `kind` lets the modal split them into Receipts/Payments groups; absent (legacy
// puracct, payments only) is treated as 'payment'.
export interface TransferSelectableItem {
    id: string;
    description: string;
    amountCents: number;
    kind?: 'receipt' | 'payment';
}

// Payload for the transfer item-selection modal. Built by puracct's
// openTransferForm() and delivered via the topWindowTransferItemSelection
// bridge (and by the Closing Dashboard's Transfer button). The user totals up
// the selected `items` across both sections; the sum pre-fills the trust
// transfer amount (see useLaunchTransferForm).
export interface TransferItemSelectionPayload {
    appId: number;
    fileNumber: string;
    clientName: string;
    fileCreationDate: string;
    items: TransferSelectableItem[];
}

export interface MultiFileExportFileRef {
    appId: number;
    fileNumber: string;
    clientName: string;
    fileCreationDate: string;
}

export interface MultiFileExportPayload {
    type: 'deposit' | 'withdrawal';
    files: MultiFileExportFileRef[];
}

export interface TrustShieldBankAccount {
    id: string;
    account_name: string;
    account_type: string;
    account_number?: string;
}

export type RealtiRowKind =
    | 'receipt'
    | 'disbursement-check'
    | 'disbursement-non-check'
    | 't2t-transfer-leg'
    | 'm2m-transfer-immediate'
    | 'withdrawal-request'
    | 'transfer-request-t2t'
    | 'transfer-request-m2m'
    | 'reversal';

export type RealtiBankStatus = 'PENDING' | 'CLEARED' | 'RECONCILED' | 'VOID';

export type RealtiCheckStatus =
    | 'PENDING'
    | 'PRINTED'
    | 'CLEARED'
    | 'VOID'
    | 'STALE'
    | 'STOP_PAYMENT';

export interface TrustShieldExportRecord {
    hash: string;
    type: ItemType;
    status: string;
    amount_cents: number;
    description: string;
    created_at: string;
    transaction_date?: string;
    matter_file_number: string;
    withdrawal_request_id?: string;

    // Row-actions matrix fields (server adds these on /exports/batch).
    // All optional — undefined means an older deploy without the matrix
    // projection; treat undefined the same as null.
    kind?: RealtiRowKind;
    id?: string;
    bankTransactionId?: string | null;
    bankStatus?: RealtiBankStatus | null;
    checkStatus?: RealtiCheckStatus | null;
    // CheckRegister UUID, required on disbursement-check rows. Distinct from
    // bankTransactionId — print-check routes by this id.
    checkRegisterId?: string | null;
    paymentMethod?: string | null;
    referenceNumber?: string | null;
    requestedByUserId?: string | null;
    linkedInvoicePayment?: boolean;
    linkedBillPayment?: boolean;
    reversesTransactionId?: string | null;
    isVoided?: boolean;

    // TS-internal transaction id and edit-chain pointers. The original
    // transaction (the one Realti exported) carries the hash. When the user
    // edits it in TrustShield, the original is voided and a successor is
    // created with edited_from_transaction_id pointing back to it; voids
    // create a reversal with voids_transaction_id. Realti uses these to
    // distinguish a true void from an edit and to walk the chain.
    transaction_id?: number;
    edited_from_transaction_id?: number | null;
    voids_transaction_id?: number | null;

    // Server-resolved "current effective" state for this hash. Always
    // populated by the server post-API-update — when the item is untouched,
    // mirrors the original. is_edited flags edit chains, is_truly_voided
    // flags manual voids (no successor). chain_depth = number of edits
    // performed since the original (0 if untouched or voided).
    effective?: TrustShieldEffective;
}

export interface TrustShieldEffective {
    transaction_id: number;
    amount_cents: number;
    status: string;
    kind?: RealtiRowKind | null;
    bankStatus?: RealtiBankStatus | null;
    checkStatus?: RealtiCheckStatus | null;
    paymentMethod?: string | null;
    isVoided: boolean;
    is_edited: boolean;
    is_truly_voided: boolean;
    chain_depth: number;
}

// Payload for the discrepancy modal — populated by puracct.xml when the
// user clicks the alert icon next to a row whose Realti amount differs
// from TrustShield's current effective amount. View-only: tells the user
// where the values diverged and how to reconcile.
export interface TrustShieldDiscrepancyPayload {
    realtiAmountCents: number;
    trustAmountCents: number;
    wasEdited: boolean;
    chainDepth: number;
    rowDescription?: string;
}

// A single trust-ledger transaction that exists in TrustShield but has no
// Realti linkage (created directly in TrustShield). Display-only — populated
// by the trust API's /matters/:appId/unlinked-transactions endpoint and
// rendered read-only in TrustLedgerDiffModal.
export interface TrustLedgerDiffItem {
    type: 'DEPOSIT' | 'WITHDRAWAL' | 'TRANSFER';
    amount_cents: number;
    description: string;
    transaction_date?: string;
    created_at?: string;
    status?: string;
    counterparty?: string;
}

export interface TrustLedgerDiffPayload {
    receipts: TrustLedgerDiffItem[];
    payments: TrustLedgerDiffItem[];
}

export interface TrustShieldWithdrawalGroup {
    payee_name: string;
    payee_address: string;
    totalCents: number;
    items: TrustShieldExportItem[];
}

export interface DepositExportResult {
    hash: string;
    success: boolean;
    status?: string;
    error?: string;
    alreadyExisted?: boolean;
}

export interface DepositRowFormData {
    amountCents: number;
    transactionDate: string;
    paymentMethod: string;
    receivedFrom: string;
    referenceNumber: string;
}

export type ModalPhase = 'authenticating' | 'ready' | 'exporting' | 'results' | 'error';

export interface SingleDepositFormData {
    bankAccountId: string;
    amountCents: number;
    transactionDate: string;       // ISO date, defaults to today
    paymentMethod: string;         // CHECK, WIRE_TRANSFER, EFT_ACH, etc.
    description: string;
    payeePayor: string;            // "Received From"
    referenceNumber: string;
    alreadyDeposited: boolean;     // If true, deposit confirmed at bank → CLEARED; otherwise PENDING
}

export const PAYMENT_METHODS = [
    { value: 'CHECK', label: 'Check' },
    { value: 'WIRE_TRANSFER', label: 'Wire Transfer' },
    { value: 'EFT_ACH', label: 'EFT / ACH' },
    { value: 'CASH', label: 'Cash' },
    { value: 'BANK_DRAFT', label: 'Bank Draft' },
    { value: 'CREDIT_CARD', label: 'Credit Card' },
    { value: 'OTHER', label: 'Other' },
] as const;

